import { ITelemetry } from '@msdyn365-commerce/telemetry-internal';
import React, { useCallback, useRef, useState } from 'react';
import classnames from 'classnames';
import { Skeleton } from '../skeleton/skeleton';

export interface VideoPlaybackHandle {
    play: () => void;
}

interface Props {
    src: string;
    poster?: string;
    isPriority?: boolean;
    isPausable?: boolean;
    playLabel: string;
    pauseLabel: string;
    videoRef?: (handle: VideoPlaybackHandle | null) => void;
    wrapperClassname?: string;
    className?: string;
    onClick?: () => void;
    location: string;
    telemetry: ITelemetry;
    variantConcatenatedId?: string;
}

const HAVE_CURRENT_DATA = 2;
const VISIBILITY_THRESHOLD = 0.5;

// Handles video autoplay and pause while being hidden. Needed for Safari, works by default on other browsers.
// Pausable videos are never auto-played, only paused.
const observeVideoVisibility = (video: HTMLVideoElement, allowAutoPlay: boolean): IntersectionObserver | undefined => {
    if (typeof IntersectionObserver === 'undefined') {
        return;
    }

    const observer = new IntersectionObserver(
        ([entry]) => {
            if (entry.isIntersecting) {
                if (allowAutoPlay) {
                    video.play().catch(() => {});
                }
            } else {
                video.pause();
            }
        },
        { threshold: VISIBILITY_THRESHOLD }
    );
    observer.observe(video);
    return observer;
};

export const VideoWithLoading = ({
    src,
    poster,
    isPriority = false,
    isPausable = false,
    playLabel,
    pauseLabel,
    videoRef,
    wrapperClassname,
    className,
    onClick,
    location,
    telemetry,
    variantConcatenatedId
}: Props) => {
    const [isLoaded, setIsLoaded] = useState(false);
    // Pausable videos start paused
    const [isPaused, setIsPaused] = useState(isPausable);
    const observerRef = useRef<IntersectionObserver>();

    // Keep the latest videoRef callback in a ref so handleVideoRef stays stable
    const videoRefLatest = useRef(videoRef);
    videoRefLatest.current = videoRef;

    const handleVideoRef = useCallback(
        (node: HTMLVideoElement | null) => {
            videoRefLatest.current?.(
                node && {
                    play: () => {
                        setIsPaused(false);
                        node.play().catch(() => setIsPaused(true));
                    }
                }
            );

            observerRef.current?.disconnect();
            observerRef.current = undefined;

            if (!node) {
                return;
            }
            // The video may already have data before hydration, in which case loadedData never fires
            if (node.readyState >= HAVE_CURRENT_DATA) {
                setIsLoaded(true);
            }
            observerRef.current = observeVideoVisibility(node, !isPausable);
        },
        [isPausable]
    );

    const handleError = () => {
        telemetry.error(
            `[video-with-loading][${location}] Video failed to load (variantId: ${variantConcatenatedId ?? 'unknown'}) (src: ${src})`
        );
        setIsLoaded(true);
    };

    const handleOnLoadedData = () => {
        setIsLoaded(true);
    };

    const togglePlayback = (video: HTMLVideoElement) => {
        if (video.paused) {
            video.play().catch(() => {});
        } else {
            video.pause();
        }
    };

    const handleVideoClick = (event: React.MouseEvent<HTMLVideoElement>) => {
        if (isPausable) {
            togglePlayback(event.currentTarget);
        }
    };

    const handleVideoKeyDown = (event: React.KeyboardEvent<HTMLVideoElement>) => {
        if (isPausable && (event.key === 'Enter' || event.key === ' ')) {
            event.preventDefault();
            togglePlayback(event.currentTarget);
        }
    };

    return (
        <div
            className={classnames('video-with-loading', wrapperClassname, { 'video-with-loading--pausable': isPausable })}
            onClick={onClick}
        >
            {!isLoaded && <Skeleton className='video-with-loading__skeleton' />}
            <video
                ref={handleVideoRef}
                className={classnames('video-with-loading__video', className, {
                    'video-with-loading__video--loaded': isLoaded
                })}
                src={src}
                poster={poster}
                autoPlay={isPriority && !isPausable}
                loop
                muted
                playsInline
                preload={isPriority ? 'auto' : 'metadata'}
                role={isPausable ? 'button' : undefined}
                tabIndex={isPausable ? 0 : undefined}
                aria-label={isPausable ? (isPaused ? playLabel : pauseLabel) : undefined}
                onLoadedData={handleOnLoadedData}
                onError={handleError}
                onClick={handleVideoClick}
                onKeyDown={handleVideoKeyDown}
                onPlay={() => setIsPaused(false)}
                onPause={() => setIsPaused(true)}
            />
            {isPausable && isPaused && <div className='video-with-loading__pause-overlay' />}
        </div>
    );
};
